import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MenuItem } from '@mui/material';
import { useToast } from 'use-toast-mui';
import { getIotBranches, setupBranchIot, resyncBranchIot, deleteBranchIot } from '../../../api/iot';
import { errorMessages } from '../../../utils/methods';
import {
  IotSetupPanel as Panel,
  IotSetupLabel,
  IotSetupInfo,
  IotSetupSelect,
  IotSetupButton,
  IotSetupDangerButton,
} from './FacilityControlStyled';

// 총관리자 전용. 현재 로그인 지점을 Elyxion 지점과 연동/재동기화/해제한다.
const IotSetupPanel = ({ branchId, token, branchIot, rooms, sensors }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedIotBranchId, setSelectedIotBranchId] = useState('');

  const isMapped = !!branchIot?.iotBranchId;

  const { data: iotBranches = [], isLoading: isBranchesLoading } = useQuery({
    queryKey: ['iotBranches'],
    queryFn: () => getIotBranches(token),
    enabled: !isMapped && !!token,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['branchIot', branchId] });
    queryClient.invalidateQueries({ queryKey: ['iotRoom', branchId] });
    queryClient.invalidateQueries({ queryKey: ['iotSensor', branchId] });
  };

  const summary = res =>
    t('룸 {{rooms}}개 · 센서 {{sensors}}개 (온라인 {{online}}개)', {
      rooms: res?.roomCount ?? 0,
      sensors: res?.sensorCount ?? 0,
      online: res?.onlineSensorCount ?? 0,
    });

  const setupMutation = useMutation({
    mutationFn: () => setupBranchIot({ branchId, iotBranchId: selectedIotBranchId }, token),
    onSuccess: res => {
      toast.success(`${t('IoT 연동이 완료되었습니다')}. ${summary(res)}`);
      setSelectedIotBranchId('');
      refresh();
    },
    onError: error => toast.error(errorMessages(error) || t('IoT 연동에 실패했습니다')),
  });

  const resyncMutation = useMutation({
    mutationFn: () => resyncBranchIot(branchId, token),
    onSuccess: res => {
      toast.success(`${t('재동기화가 완료되었습니다')}. ${summary(res)}`);
      refresh();
    },
    onError: error => toast.error(errorMessages(error) || t('재동기화에 실패했습니다')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBranchIot(branchId, token),
    onSuccess: () => {
      toast.success(t('IoT 연동이 해제되었습니다'));
      refresh();
    },
    onError: error => toast.error(errorMessages(error) || t('IoT 연동 해제에 실패했습니다')),
  });

  const handleDelete = () => {
    if (!window.confirm(t('연동을 해제하면 룸·센서·릴레이·전원 스케줄·전원 예약이 모두 삭제됩니다. 해제하시겠습니까?'))) return;
    deleteMutation.mutate();
  };

  const busy = setupMutation.isPending || resyncMutation.isPending || deleteMutation.isPending;

  if (!isMapped) {
    return (
      <Panel>
        <IotSetupLabel>{t('IoT 연동 설정')}</IotSetupLabel>
        <IotSetupInfo>{t('이 지점은 아직 Elyxion 지점과 연동되지 않았습니다.')}</IotSetupInfo>
        <IotSetupSelect
          size="small"
          displayEmpty
          value={selectedIotBranchId}
          onChange={e => setSelectedIotBranchId(e.target.value)}
          disabled={isBranchesLoading || busy}
        >
          <MenuItem value="" disabled>
            {isBranchesLoading ? t('불러오는 중...') : t('Elyxion 지점 선택')}
          </MenuItem>
          {iotBranches.map(b => (
            <MenuItem key={b.branchId} value={b.branchId}>
              {b.branchNm}
            </MenuItem>
          ))}
        </IotSetupSelect>
        <IotSetupButton onClick={() => setupMutation.mutate()} disabled={!selectedIotBranchId || busy}>
          {setupMutation.isPending ? t('연동 중...') : t('연동')}
        </IotSetupButton>
      </Panel>
    );
  }

  const onlineCount = (sensors || []).filter(s => s.connectYn === 'Y').length;

  return (
    <Panel>
      <IotSetupLabel>{t('IoT 연동 설정')}</IotSetupLabel>
      <IotSetupInfo>
        {t('연동 지점')}: <b>{branchIot.iotBranchNm}</b> ·{' '}
        {t('룸 {{rooms}}개 · 센서 {{sensors}}개 (온라인 {{online}}개)', {
          rooms: rooms?.length ?? 0,
          sensors: sensors?.length ?? 0,
          online: onlineCount,
        })}
      </IotSetupInfo>
      <IotSetupButton onClick={() => resyncMutation.mutate()} disabled={busy}>
        {resyncMutation.isPending ? t('동기화 중...') : t('재동기화')}
      </IotSetupButton>
      <IotSetupDangerButton onClick={handleDelete} disabled={busy}>
        {deleteMutation.isPending ? t('해제 중...') : t('연동 해제')}
      </IotSetupDangerButton>
    </Panel>
  );
};

export default IotSetupPanel;
